import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Select from "common-ui-components/components/select";
import PhoneInput from "common-ui-components/components/phone-input/PhoneInput";
import styles from "../SupportForm.module.css";
import { MAIL_TYPE, sendSupportMail, normalizePhone } from "../../api";

const CRITICALITY_OPTIONS = [
  { value: "low", label: "Low: General question", header: "General question" },
  {
    value: "medium",
    label: "Medium: Feature not working",
    header: "Feature not working",
  },
  {
    value: "high",
    label: "High: I'm stuck, need assistance",
    header: "I'm stuck, need assistance",
  },
];

const Contact = forwardRef((_, ref) => {
  const formRef = useRef(null);
  const fileInputRef = useRef(null);

  const [subject, setSubject] = useState("");
  const [criticality, setCriticality] = useState("medium");
  const [description, setDescription] = useState("");
  const [mobile, setMobile] = useState("");
  const [file, setFile] = useState(null);

  const criticalityOptions = CRITICALITY_OPTIONS.map((opt) => opt.label);
  const activeCriticalityIndex = CRITICALITY_OPTIONS.findIndex(
    (opt) => opt.value === criticality,
  );

  const handleFileChange = (e) => {
    setFile(e.target.files?.[0] ?? null);
  };

  const handleClearFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useImperativeHandle(ref, () => ({
    submit: async () => {
      if (formRef.current && !formRef.current.reportValidity()) {
        return { success: false };
      }
      const selectedCriticality =
        CRITICALITY_OPTIONS.find((opt) => opt.value === criticality) ??
        CRITICALITY_OPTIONS[1]; // fallback to "medium"

      try {
        await sendSupportMail({
          mailType: MAIL_TYPE.CONTACT_SUPPORT,
          attachment: file,
          payload: {
            contactSupportDTO: {
              subject,
              criticality,
              header: selectedCriticality.header,
              issueDescription: description,
              mobileNumber: normalizePhone(mobile),
            },
          },
        });
        return { success: true };
      } catch (err) {
        return { success: false, error: err.message };
      }
    },
  }));

  return (
    <form
      ref={formRef}
      className={styles.form}
      onSubmit={(e) => e.preventDefault()}
    >
      <div className={styles.field}>
        <label className={styles.label} htmlFor="subject">
          Subject<span className={styles.required}>*</span>
        </label>
        <input
          id="subject"
          type="text"
          className={styles.input}
          placeholder="e.g. Devices stuck at 0% scan progress"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          required
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="criticality">
          How critical is this?
        </label>
        <div className={styles.selectWrapper}>
          <Select
            options={criticalityOptions}
            active={Math.max(activeCriticalityIndex, 0)}
            onOptionSelect={(_option, index) =>
              setCriticality(CRITICALITY_OPTIONS[index]?.value ?? criticality)
            }
            containerProps={{ className: styles.selectDropdown }}
            triggerProps={{ id: "criticality", className: styles.select }}
          />
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="description">
          Describe the issue<span className={styles.required}>*</span>
        </label>
        <textarea
          id="description"
          className={styles.textarea}
          placeholder="What happened, and since when?"
          rows={5}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
        />
      </div>

      <PhoneInput
        id="mobile"
        label="Mobile number"
        value={mobile}
        onChange={setMobile}
      />

      <div className={styles.field}>
        <label className={styles.label}>Attachments</label>
        <div className={styles.attachmentBox}>
          <label className={styles.chooseFileButton}>
            Choose file
            <input
              type="file"
              className={styles.hiddenFileInput}
              ref={fileInputRef}
              onChange={handleFileChange}
            />
          </label>
          <span className={styles.attachmentHint}>
            {file
              ? file.name
              : "Screenshots or logs help us resolve this faster, up to 10 MB"}
          </span>
          {file && (
            <button
              type="button"
              className={styles.clearFileButton}
              onClick={handleClearFile}
            >
              Clear
            </button>
          )}
        </div>
      </div>
    </form>
  );
});

Contact.displayName = "Contact";
export default Contact;
